import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class SocialGame extends Phaser.Scene {
  constructor() {
    super({ key: 'SocialGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.firstSelected = null;
    this.matchedPairs = 0;
    this.isGameOver = false;

    if (this.difficulty === 'easy') {
      this.totalPairs = 4; // 8 cards (2x4)
      this.timeLimit = 30;
      this.cols = 2;
    } else if (this.difficulty === 'medium') {
      this.totalPairs = 6; // 12 cards (3x4)
      this.timeLimit = 25;
      this.cols = 3;
    } else {
      this.totalPairs = 8; // 16 cards (4x4)
      this.timeLimit = 20;
      this.cols = 4;
    }
    this.timeLeft = this.timeLimit;
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x221326);

    // HUD
    this.matchText = this.add.text(16, 16, `Matches: ${this.matchedPairs}/${this.totalPairs}`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FF4081'
    });

    this.timerText = this.add.text(width - 16, 16, `⏱️ ${this.timeLeft}s`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#FFD700'
    }).setOrigin(1, 0);

    // Build Tile Grid
    this.createEmoteGrid();

    // Timer Event
    this.timerEvent = this.time.addEvent({
      delay: 1000,
      callback: () => {
        if (this.isGameOver) return;
        this.timeLeft--;
        this.timerText.setText(`⏱️ ${this.timeLeft}s`);
        if (this.timeLeft <= 0) {
          this.triggerEnd(false);
        }
      },
      loop: true
    });
  }

  createEmoteGrid() {
    const { width, height } = this.cameras.main;
    const allEmotes = ['💖', '🥳', '🐶', '🍕', '🐱', '🌸', '🎁', '🚀', '🌟', '🍩'];
    const chosenEmotes = Phaser.Utils.Array.Shuffle(allEmotes).slice(0, this.totalPairs);

    // Duplicate and shuffle
    const deck = Phaser.Utils.Array.Shuffle([...chosenEmotes, ...chosenEmotes]);

    const totalCards = deck.length;
    const cols = this.cols;
    const rows = Math.ceil(totalCards / cols);

    const cardW = Math.min(70, Math.floor((width - 40) / cols));
    const cardH = Math.min(80, Math.floor((height - 120) / rows));
    const startX = (width - (cols * cardW)) / 2 + cardW / 2;
    const startY = 80 + cardH / 2;

    this.cards = [];

    deck.forEach((emote, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = startX + col * cardW;
      const y = startY + row * cardH;

      const container = this.add.container(x, y);

      const cardBg = this.add.rectangle(0, 0, cardW - 8, cardH - 8, 0x38203E);
      cardBg.setStrokeStyle(2, 0xFF4081, 0.6);
      cardBg.setInteractive({ cursor: 'pointer' });

      // Emote text (hidden initially)
      const label = this.add.text(0, 0, emote, { fontSize: '26px' }).setOrigin(0.5);
      label.setVisible(false);

      const backCover = this.add.text(0, 0, '❓', { fontSize: '20px', color: '#A0A5BA' }).setOrigin(0.5);

      container.add([cardBg, label, backCover]);
      container.setData('emote', emote);
      container.setData('isOpen', false);
      container.setData('isMatched', false);
      container.setData('bg', cardBg);
      container.setData('label', label);
      container.setData('cover', backCover);

      cardBg.on('pointerdown', () => {
        if (this.isGameOver) return;
        this.handleCardClick(container);
      });

      this.cards.push(container);
    });
  }

  handleCardClick(card) {
    if (card.getData('isOpen') || card.getData('isMatched')) return;
    if (this.isChecking) return;

    sound.playClick();
    this.openCard(card);

    if (!this.firstSelected) {
      this.firstSelected = card;
    } else {
      // Second card selected
      const first = this.firstSelected;
      const second = card;
      this.firstSelected = null;

      if (first.getData('emote') === second.getData('emote')) {
        // MATCH!
        first.setData('isMatched', true);
        second.setData('isMatched', true);
        this.matchedPairs++;
        this.matchText.setText(`Matches: ${this.matchedPairs}/${this.totalPairs}`);

        sound.playCombo(this.matchedPairs);

        // Flash green
        first.getData('bg').setStrokeStyle(3, 0x00E676, 1);
        second.getData('bg').setStrokeStyle(3, 0x00E676, 1);

        if (this.matchedPairs >= this.totalPairs) {
          this.time.delayedCall(400, () => this.triggerEnd(true));
        }
      } else {
        // MISMATCH
        this.isChecking = true;
        sound.playTone(200, 'sawtooth', 0.15, 0.1, -40);
        first.getData('bg').setStrokeStyle(3, 0xFF5252, 1);
        second.getData('bg').setStrokeStyle(3, 0xFF5252, 1);

        this.time.delayedCall(700, () => {
          this.closeCard(first);
          this.closeCard(second);
          this.isChecking = false;
        });
      }
    }
  }

  openCard(card) {
    card.setData('isOpen', true);
    card.getData('cover').setVisible(false);
    card.getData('label').setVisible(true);
    card.getData('bg').setFillStyle(0x562B60);
  }

  closeCard(card) {
    card.setData('isOpen', false);
    card.getData('cover').setVisible(true);
    card.getData('label').setVisible(false);
    card.getData('bg').setFillStyle(0x38203E);
    card.getData('bg').setStrokeStyle(2, 0xFF4081, 0.6);
  }

  triggerEnd(isWin) {
    this.isGameOver = true;
    this.timerEvent.remove();

    const { width, height } = this.cameras.main;
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.8);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '💖 HEARTWARMING!', {
        fontSize: '22px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} SOCIAL BARS GAINED! 💬`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '💔 TIME RAN OUT', {
        fontSize: '22px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, `Connected ${this.matchedPairs}/${this.totalPairs} pairs`, {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0xFF4081)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#FFF'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
